<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import { FIELD_CLASS } from '../constants';
import { apiErrorMessage } from '../../../../api/realEstate/errors';

// Opening hours are edited on the Booking section now (Layer 0 opening
// hours drives `showroom.opening_hours_text`, which is server-generated
// and read-only) — this modal only covers the showroom's contact fields.
export default {
  name: 'SalesModal',
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      form: {
        showroom_address: this.project.showroom.address || '',
        showroom_phone: this.project.showroom.phone || '',
        showroom_waze_url: this.project.showroom.waze_url || '',
        showroom_maps_url: this.project.showroom.maps_url || '',
      },
      isSubmitting: false,
    };
  },
  methods: {
    ...mapActions('realEstate', ['patchProject']),
    async handleSubmit() {
      this.isSubmitting = true;
      try {
        await this.patchProject({
          propertyId: this.project.property_id,
          fields: { ...this.form },
        });
        useAlert(this.$t('REAL_ESTATE.SALES.SUCCESS_UPDATED'));
        this.$emit('close');
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'REAL_ESTATE.SALES.ERROR_UPDATE'));
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-lg max-h-[85vh] overflow-y-auto"
    >
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{ $t('REAL_ESTATE.SALES.MODAL_TITLE') }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <form class="space-y-4" @submit.prevent="handleSubmit">
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.SALES.FIELD_ADDRESS') }}
          </label>
          <input
            v-model="form.showroom_address"
            :placeholder="$t('REAL_ESTATE.SALES.FIELD_ADDRESS_PLACEHOLDER')"
            type="text"
            :class="fieldClass"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.SALES.FIELD_PHONE') }}
          </label>
          <input
            v-model="form.showroom_phone"
            :placeholder="$t('REAL_ESTATE.SALES.FIELD_PHONE_PLACEHOLDER')"
            type="text"
            :class="fieldClass"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.SALES.FIELD_WAZE') }}
          </label>
          <input
            v-model="form.showroom_waze_url"
            :placeholder="$t('REAL_ESTATE.SALES.FIELD_WAZE_PLACEHOLDER')"
            type="url"
            :class="fieldClass"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.SALES.FIELD_MAPS') }}
          </label>
          <input
            v-model="form.showroom_maps_url"
            :placeholder="$t('REAL_ESTATE.SALES.FIELD_MAPS_PLACEHOLDER')"
            type="url"
            :class="fieldClass"
          />
        </div>
        <p class="text-xs text-slate-400">
          {{ $t('REAL_ESTATE.SALES.HOURS_EDIT_NOTE') }}
        </p>

        <div
          class="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-200 dark:border-slate-700"
        >
          <woot-button variant="clear" type="button" @click="$emit('close')">
            {{ $t('REAL_ESTATE.SALES.CANCEL') }}
          </woot-button>
          <woot-button type="submit" :is-loading="isSubmitting">
            {{ $t('REAL_ESTATE.SALES.SAVE') }}
          </woot-button>
        </div>
      </form>
    </div>
  </div>
</template>
